import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ExtractionQAFileData, ExtractionQAStatsGet, QAStatsOverviewGet, QAAISummaryRequest, QAAISummaryResponse, FieldErrorRatesResponse, JSONGeneratorHistoryElectricityCreate, JSONGeneratorHistoryElectricityGet, JSONGeneratorHistoryGasCreate, JSONGeneratorHistoryGasGet } from '../interfaces/admin-interfaces';
import { API_ROUTES } from './api-routes';
import { DEFAULT_JSON_HEADERS } from './headers.config';

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  constructor(private http: HttpClient) { }

  createElectricityJsonRecord(data: JSONGeneratorHistoryElectricityCreate): Observable<any> {
    const payload = JSON.stringify(data);

    return this.http.post<JSONGeneratorHistoryElectricityGet>(
      `${API_ROUTES.ADMIN.JSON_GENERATOR_ELECTRICITY}`,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  createGasJsonRecord(data: JSONGeneratorHistoryGasCreate): Observable<any> {
    const payload = JSON.stringify(data);

    return this.http.post<JSONGeneratorHistoryGasGet>(
      `${API_ROUTES.ADMIN.JSON_GENERATOR_GAS}`,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  updateElectricityJsonRecord(id: string, data: JSONGeneratorHistoryElectricityCreate): Observable<any> {
    const payload = JSON.parse(JSON.stringify(data));

    return this.http.post<JSONGeneratorHistoryElectricityGet>(
      `${API_ROUTES.ADMIN.JSON_GENERATOR_ELECTRICITY}/${id}`,
      payload,
    );
  }

  updateGasJsonRecord(id: string, data: JSONGeneratorHistoryGasCreate): Observable<any> {
    const payload = JSON.parse(JSON.stringify(data));

    return this.http.post<JSONGeneratorHistoryGasGet>(
      `${API_ROUTES.ADMIN.JSON_GENERATOR_GAS}/${id}`,
      payload,
    );
  }

  deleteElectricityJsonRecord(id: string): Observable<any> {
    return this.http.delete<any>(
      `${API_ROUTES.ADMIN.JSON_GENERATOR_ELECTRICITY}/${id}`,
    );
  }

  deleteGasJsonRecord(id: string): Observable<any> {
    return this.http.delete<any>(
      `${API_ROUTES.ADMIN.JSON_GENERATOR_GAS}/${id}`,
    );
  }

  getElectricityJsonRecords(next_page: number = 1, per_page: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    return this.http.get<any>(
      API_ROUTES.ADMIN.JSON_GENERATOR_ELECTRICITY,
      { params: params, observe: 'response' }
    ).pipe(
      map(response => {
        const totalCount = response.headers?.get('x-total-count') ? Number(response.headers.get('x-total-count')) : 0;
        return { data: response.body.data || [], totalCount: totalCount };
      }));
  }

  getGasJsonRecords(next_page: number = 1, per_page: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    return this.http.get<any>(
      API_ROUTES.ADMIN.JSON_GENERATOR_GAS,
      { params: params, observe: 'response' }
    ).pipe(
      map(response => {
        const totalCount = response.headers?.get('x-total-count') ? Number(response.headers.get('x-total-count')) : 0;
        return { data: response.body.data || [], totalCount: totalCount };
      }));
  }

  uploadElectricityJsonRecord(file: File): Observable<any> {
    const formData = new FormData();
    formData.append("file", file);

    return this.http.post<any>(
      `${API_ROUTES.ADMIN.JSON_GENERATOR_ELECTRICITY}/upload/`,
      formData,
    );
  }

  uploadGasJsonRecord(file: File): Observable<any> {
    const formData = new FormData();
    formData.append("file", file);

    return this.http.post<any>(
      `${API_ROUTES.ADMIN.JSON_GENERATOR_GAS}/upload/`,
      formData,
    );
  }

  // File Extraction
  getFileExtractionHistory(next_page: number = 1, per_page: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    return this.http.get<any>(
      API_ROUTES.ADMIN.EXTRACTION_HISTORY,
      { params: params, observe: 'response' }
    ).pipe(
      map(response => {
        const totalCount = response.headers?.get('x-total-count') ? Number(response.headers.get('x-total-count')) : 0;
        return { data: response.body.data || [], totalCount: totalCount };
      }));
  }

  uploadFileForExtraction(file: File): Observable<any> {
    const formData = new FormData();
    formData.append("file", file);

    return this.http.post<any>(
      `${API_ROUTES.ADMIN.EXTRACTION}/upload`,
      formData,
    );
  }

  getExtractionEventLogs(id: string): Observable<any> {
    const payload = {};

    return this.http.get<any>(
      `${API_ROUTES.ADMIN.EXTRACTION}/${id}/logs`,
      payload,
    );
  }

  // Extraction QA
  getQAStats(next_page: number = 1, per_page: number = 20, invoice_period_type?: string): Observable<{ data: ExtractionQAStatsGet[], totalCount: number }> {
    let params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    if (invoice_period_type) {
      params = params.set('invoice_period_type', invoice_period_type);
    }

    return this.http.get<any>(
      API_ROUTES.ADMIN.QA_STATS,
      { params: params, observe: 'response' }
    ).pipe(
      map(response => {
        const totalCount = response.headers?.get('x-total-count') ? Number(response.headers.get('x-total-count')) : 0;
        return { data: response.body.data || [], totalCount: totalCount };
      }));
  }

  getQAFileData(fileId: string, invoice_period_type?: string): Observable<ExtractionQAFileData> {
    let params = new HttpParams();
    if (invoice_period_type) {
      params = params.set('invoice_period_type', invoice_period_type);
    }

    return this.http.get<ExtractionQAFileData>(
      `${API_ROUTES.ADMIN.QA_FILE}/${fileId}`,
      { params }
    );
  }

  getQAAISummary(data: QAAISummaryRequest): Observable<QAAISummaryResponse> {
    const payload = JSON.stringify(data);

    return this.http.post<QAAISummaryResponse>(
      API_ROUTES.ADMIN.QA_AI_SUMMARY,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  getQAStatsOverview(invoice_period_type?: string): Observable<QAStatsOverviewGet> {
    let params = new HttpParams();
    if (invoice_period_type) {
      params = params.set('invoice_period_type', invoice_period_type);
    }

    return this.http.get<QAStatsOverviewGet>(
      API_ROUTES.ADMIN.QA_STATS_OVERVIEW,
      { params }
    );
  }

  getFieldErrorRates(fromDate: string, toDate: string, energyType?: string, supplier?: string, extractedBy?: string, invoice_period_type?: string): Observable<FieldErrorRatesResponse> {
    let params = new HttpParams()
      .set('from_date', fromDate)
      .set('to_date', toDate);

    if (energyType) {
      params = params.set('energy_type', energyType);
    }
    if (supplier) {
      params = params.set('supplier', supplier);
    }
    if (extractedBy) {
      params = params.set('extracted_by', extractedBy);
    }
    if (invoice_period_type) {
      params = params.set('invoice_period_type', invoice_period_type);
    }

    return this.http.get<FieldErrorRatesResponse>(
      API_ROUTES.ADMIN.QA_FIELD_ERRORS,
      { params }
    );
  }
}
